import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/EnergyField';
import { EvidenceLabel } from '../components/EvidenceLabel';
import { EVIDENCE_ORDER, RELATIONSHIP_META, evidenceMeta } from '../lib/evidence';
import { ArrowLeft, ArrowRight, ScrollText, GitBranch, ShieldCheck, Eye } from 'lucide-react';

const rise = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' }, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } };

const Section = ({ label, title, children }) => (
  <motion.section {...rise} className="mb-16">
    <p className="mono-label mb-3">{label}</p>
    <h2 className="font-serif-display text-3xl lg:text-4xl font-light mb-5">{title}</h2>
    <div className="space-y-4 font-sans-tech text-[#AEB6C4] leading-[1.9]">{children}</div>
  </motion.section>
);

const Methodology = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#08080A] text-[#E2E4E9]">
      <nav className="sticky top-0 z-50 glass-hud border-b border-[#262A36]">
        <div className="container mx-auto px-6 lg:px-12 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')}><Logo /></button>
          <button onClick={() => navigate(-1)} className="btn-quiet !py-2 !px-5" data-testid="method-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        </div>
      </nav>

      <div className="relative w-full h-48 lg:h-56 overflow-hidden border-b border-[#262A36]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1512] to-[#08080A]" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="container mx-auto px-6 lg:px-12 pb-6 max-w-3xl">
            <p className="mono-meta text-[#26B0A8] flex items-center gap-2"><ScrollText className="w-4 h-4" /> Research Method · Public</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 py-14 max-w-3xl" data-testid="methodology-page">
        <motion.div {...rise} className="mb-14">
          <h1 className="font-serif-display text-4xl lg:text-6xl font-light leading-[1.08] mb-6">How the Archive<br /><span className="gradient-text italic">Classifies Evidence</span></h1>
          <p className="font-sans-tech text-lg text-[#949BAA] leading-relaxed">
            FreeFieldFlow collects both established and exploratory research. Records are not treated as equally
            proven. This page explains, plainly, how we label what we archive — and what our connections do and do not claim.
          </p>
        </motion.div>

        <Section label="Principle" title="Uncertainty is visible on purpose">
          <p>
            The archive spans mature engineering and speculative inquiry. If both were presented identically,
            preliminary material would borrow authority it has not earned. To prevent that, every record carries a
            visible <span className="text-[#C8CDD6]">evidence class</span> — a short, muted label describing the maturity
            of the available evidence, not a verdict on truth.
          </p>
          <p>Speculation is retained when it is useful — but it is labelled as speculation. The archive does not convert hypothesis into fact.</p>
        </Section>

        <Section label="Part 1" title="Evidence classes">
          <p>Each record is assigned one of seven classes, ordered from strongest to weakest available evidence:</p>
          <div className="not-prose space-y-3 mt-4" data-testid="method-evidence-list">
            {EVIDENCE_ORDER.map((k) => (
              <div key={k} className="glass-panel rounded-lg px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5" data-testid={`method-ev-${k.toLowerCase()}`}>
                <EvidenceLabel evidenceClass={k} className="shrink-0" />
                <span className="font-sans-tech text-sm text-[#949BAA] leading-relaxed">{evidenceMeta(k)?.note}</span>
              </div>
            ))}
          </div>
          <p className="mt-4">
            Classes are assigned against consistent criteria: source quality (peer review, institution, primary vs
            secondary), evidence type (theory, observation, controlled experiment, field test, standard), replication,
            measurement quality, claim strength, reproducibility, and consistency across sources. Evidence classes may
            change as new evidence appears.
          </p>
        </Section>

        <Section label="Part 2" title="Connections are relationships, not proof">
          <p className="flex items-start gap-3"><GitBranch className="w-5 h-5 text-[#26B0A8] shrink-0 mt-1" /> Semantic similarity is not scientific validity. Co-occurrence is not causality. Shared vocabulary is not a shared mechanism. So a suggested connection never simply &ldquo;exists&rdquo; — it carries an epistemic status.</p>
          <p>We record two separate numbers: <span className="text-[#C8CDD6]">semantic similarity</span> (how conceptually related two records are) and <span className="text-[#C8CDD6]">relationship confidence</span> (how justified the stated relationship is). They are deliberately not the same. Each connection also names its type:</p>
          <div className="not-prose grid sm:grid-cols-2 gap-2 mt-4">
            {Object.entries(RELATIONSHIP_META).filter(([k]) => k !== 'REJECTED').map(([k, m]) => (
              <div key={k} className="glass-panel rounded-lg px-4 py-3">
                <p className="mono-meta text-[#26B0A8] mb-1">{m.label}</p>
                <p className="font-sans-tech text-xs text-[#6B7280] leading-relaxed">{m.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-4">Before any candidate is promoted, it is challenged with a lightweight null-model check: could a third variable explain the overlap? Is there a mechanism, or only shared words? Would the link survive removal of domain labels? Nothing becomes <span className="text-[#C8CDD6]">Direct Evidence</span> automatically.</p>
        </Section>

        <Section label="Part 3" title="AI assists, humans govern">
          <p className="flex items-start gap-3"><ShieldCheck className="w-5 h-5 text-[#26B0A8] shrink-0 mt-1" /> An AI archivist drafts summaries and recommends classifications from public research abstracts. It never invents claims, data, or citations. Every stronger relationship claim and every publication is governed by human review — an editor can inspect the reasoning, override a class, and add a curator note.</p>
          <p className="flex items-start gap-3"><Eye className="w-5 h-5 text-[#26B0A8] shrink-0 mt-1" /> Sources remain attributable. Records built only on third-party literature are labelled <span className="text-[#C8CDD6]">source synthesis</span>; original FreeFieldFlow measurement, where it exists, is labelled separately as field work.</p>
        </Section>

        <motion.div {...rise} className="glass-hud rounded-2xl p-8 lg:p-10 reflect-top scanlines text-center">
          <h3 className="font-serif-display text-3xl font-light mb-4">Read something built this way</h3>
          <p className="font-sans-tech text-[#949BAA] mb-6 max-w-md mx-auto">The Off-Grid Water Systems field guide compares twelve approaches with visible evidence strength and traceable sources.</p>
          <button onClick={() => navigate('/guides/off-grid-water-systems')} className="btn-primary" data-testid="method-guide-cta">Open the Field Guide <ArrowRight className="w-4 h-4" /></button>
        </motion.div>
      </div>
    </div>
  );
};

export default Methodology;
